import { graphql } from "gatsby"
import React, { useContext } from "react"
import useOnlineProductQuery from "../../../../gatsby-source-harvia-pim/hooks/useOnlineProductQuery"
import { PageContext } from "../../context/PageContext"
import useProductCategoryPath from "../../hooks/useProductCategoryPath"
import useProductCategoryStructure from "../../hooks/useProductCategoryStructure"
import useRoutePath from "../../hooks/useRoutePath"
import { useRouteTree } from "../../hooks/useRouteTree"
import useWebsite from "../../hooks/useWebsite"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { findDeepestCategory } from "../organisms/product/utils"
import ProductPageContent, { ProductLandingPageCta } from "./productPage/ProductPageContent"

type ProductPageProps = {
  /** Product identifier */
  sku: string
  /** Built product data */
  data?: Queries.ProductPageHarviaProductFragment
  /** Landingpage cta:s */
  landingPages?: ProductLandingPageCta[]
}

const ProductPage = (props: ProductPageProps) => {
  const { localePim } = useContext(PageContext)
  const [onlineProducts, result] = useOnlineProductQuery(props.sku ? `sku:${props.sku}` : null, {
    language: localePim,
    // fullAssociations: true,
    includeDisabledProducts: true,
  })
  const website = useWebsite()
  const getRoutePath = useRoutePath()
  const routeTree = useRouteTree(website.productRootRoute?.originalId)
  const structure = useProductCategoryStructure()

  // Muodostetaan muropolku
  function isNonNullString(val: unknown): val is string {
    return typeof val === "string"
  }
  const p = onlineProducts?.[0] || props.data
  const pCategories = p?.categories || []
  const category = findDeepestCategory(structure.subCategories, pCategories.filter(isNonNullString))
  const pim_locales = [localePim.replace("-", "_"), "en_US"] // TODO: kovakoodattu locale fallback muropolun kategoriassa
  const categoryLabel = category?.labels.find(itm => !!~pim_locales.indexOf(itm.name))?.value || category?.code || ""
  const categoryPath = useProductCategoryPath(category?.code)
  const breadcrumbBase = [
    {
      label: categoryLabel,
      path: categoryPath || `${routeTree?.[0]?.path}?search=category:${category?.code}`,
    },
    ...routeTree,
  ]

  if (!props.sku) return null

  // Ei buildattu tuote
  if (!props.data || !props.data.identifier) {
    if (!onlineProducts || !onlineProducts[0]) {
      // Tuotedataa ei vielä haettu
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          {result?.empty ? (
            <p>Product not found</p>
          ) : (
            <>
              <p>Loading product...</p>
              <LoadingSpinner loading />
            </>
          )}
        </div>
      )
    } else {
      // Online tuotesivu
      return (
        <ProductPageContent
          product={onlineProducts[0]}
          unBuiltProduct
          siteTitle={website.title}
          findDealerPath={getRoutePath(website.findDealerRoute?.originalId)}
          saunaCalculatorPath={getRoutePath(website.saunaCalculatorRoute?.originalId)}
          saunaPlannerPath={getRoutePath(website.saunaPlannerRoute?.originalId)}
          breadcrumbBase={breadcrumbBase}
          productsPageRootPath={routeTree?.[0]?.path}
          categoryLabel={categoryLabel}
        />
      )
    }
  }

  // Näytetään aluksi buildattu tuote. Käytetään päivitettyjä tietoja kun ne on noudettu.
  return (
    <ProductPageContent
      product={onlineProducts?.[0] || { ...props.data, locale: props.data.locale || "en" }}
      landingPages={props.landingPages}
      siteTitle={website.title}
      findDealerPath={getRoutePath(website.findDealerRoute?.originalId)}
      saunaCalculatorPath={getRoutePath(website.saunaCalculatorRoute?.originalId)}
      saunaPlannerPath={getRoutePath(website.saunaPlannerRoute?.originalId)}
      breadcrumbBase={breadcrumbBase}
      productsPageRootPath={routeTree?.[0]?.path}
      categoryLabel={categoryLabel}
    />
  )
}

export default ProductPage

export const query = graphql`
  fragment ProductLandingPageCta on DatoCmsProductLandingPage {
    ctaImage {
      ...DatoImageDefault
    }
    ctaLinkText
    ctaText
    ctaTitle
    originalId
  }

  fragment HarviaProductAssociationFields on HarviaProduct {
    identifier
    media {
      name
      type
      link
      thumbnail
      id
    }
    values {
      ...HarviaProductValue
    }
  }

  fragment HarviaProductValue on HarviaProductValue {
    name
    value {
      label
      # channelConversionApplied
      # locale
      metric {
        amount
        baseAmount
        unit
        baseUnit
      }
      numeric
      # optionLabel
      prices {
        amount
        currency
      }
      # scope
      text
      type
      data
    }
  }

  fragment ProductPageHarviaProduct on HarviaProduct {
    identifier
    name
    categories
    product_family
    completeness {
      locale
      missingCount
      ratio
      requiredCount
      scope
    }
    created
    databaseId
    enabled
    family
    groups
    images
    options
    type
    updated
    locale
    media {
      name
      type
      link
      thumbnail
      id
      language
    }
    values {
      ...HarviaProductValue
    }

    # Haetaan assosiaatioiden sku ja name, niin saadaan luotua ssr linkit eri tuotteiden välille
    # varsinaiset tuotekorttien tiedot haetaan frontissa dynaamisesti.
    associations {
      accessories {
        sku
        data {
          QUANTITY
        }
        product {
          identifier
          name
        }
      }
      SUBSTITUTION {
        sku
        product {
          identifier
          name
        }
      }
      Spare_parts {
        sku
        data {
          QUANTITY
        }
        product {
          identifier
          name
        }
      }
      UPSELL {
        sku
        product {
          identifier
          name
        }
      }
      compatible_items {
        sku
        data {
          QUANTITY
        }
        product {
          identifier
          name
        }
      }
      cross_sell {
        sku
        product {
          identifier
          name
        }
      }
    }
  }
`

/*
// ei haeta assosiaatioita buildissa, koska datan määrä on niin suuri

associations {
      Spare_parts {
        product {
          ...HarviaProductAssociationFields
        }
        data {
          QUANTITY
        }
      }
      UPSELL {
        product {
          ...HarviaProductAssociationFields
        }
      }
      accessories {
        product {
          ...HarviaProductAssociationFields
        }
      }
      compatible_items {
        product {
          ...HarviaProductAssociationFields
        }
      }
      cross_sell {
        product {
          ...HarviaProductAssociationFields
        }
      }
      SUBSTITUTION {
        product {
          ...HarviaProductAssociationFields
        }
      }
    }
*/
